import { useState } from 'react';
import { MessageCircle, Phone, Facebook, MapPin, X, Send, CheckCircle } from 'lucide-react';
import { BUSINESS_INFO } from '../data';

function BookingModal({ onClose }) {
  const [form, setForm]     = useState({ name: '', date: '', phone: '', message: '' });
  const [sent, setSent]     = useState(false);
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const buildMsg = () => {
    const d = form.date ? ` on ${form.date}` : '';
    return encodeURIComponent(
      `👋 Hello Isha Boutique!\n\nI'd like to book a visit${d}.\n\n` +
      `Name: ${form.name}\n` +
      `Phone: ${form.phone || 'N/A'}\n` +
      `Message: ${form.message || 'No extra notes'}\n\n` +
      `Sent from IshaFashion.com`
    );
  };

  const sendTo = (channel) => {
    setSending(true);
    const msg = buildMsg();

    let url;
    if (channel === 'whatsapp') {
      url = `https://wa.me/${BUSINESS_INFO.whatsappRaw}?text=${msg}`;
    } else {
      url = `https://m.me/${BUSINESS_INFO.messengerPageId}`;
    }

    window.open(url, '_blank');
    setSending(false);
    setSent(true);
  };

  const isValid = form.name.trim().length > 1;

  if (sent) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box glass-card" style={{ padding: '3rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(183,110,121,0.2), rgba(232,180,184,0.1))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem', color: 'var(--accent)',
          }}>
            <CheckCircle size={32} />
          </div>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.6rem', marginBottom: '0.75rem', color: 'var(--text)' }}>
            Message Sent!
          </h3>
          <p className="text-body" style={{ marginBottom: '2rem' }}>
            We'll get back to you as soon as possible. We look forward to welcoming you!
          </p>
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box glass-card"
        style={{ padding: 'clamp(1.5rem,4vw,2.5rem)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
          <div>
            <div className="badge" style={{ marginBottom: '0.75rem', width: 'fit-content' }}>Book a Visit</div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.75rem', color: 'var(--text)', fontWeight: 400 }}>
              Reserve Your Time
            </h2>
            <p className="text-body" style={{ fontSize: '0.85rem', marginTop: '0.4rem' }}>
              We'll confirm your appointment via WhatsApp or Messenger.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '0.25rem',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="form-name" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                Your Name *
              </label>
              <input
                id="form-name"
                className="input-field"
                type="text"
                placeholder="e.g. Priya Sharma"
                value={form.name}
                onChange={e => update('name', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="form-date" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                Preferred Date
              </label>
              <input
                id="form-date"
                className="input-field"
                type="date"
                value={form.date}
                onChange={e => update('date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div>
            <label htmlFor="form-phone" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
              Your Phone (optional)
            </label>
            <input
              id="form-phone"
              className="input-field"
              type="tel"
              placeholder="+39 …"
              value={form.phone}
              onChange={e => update('phone', e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="form-notes" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
              Notes (optional)
            </label>
            <textarea
              id="form-notes"
              className="input-field"
              rows={3}
              placeholder="What are you looking for? Wedding? Party? Anything else?"
              value={form.message}
              onChange={e => update('message', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Channel selection */}
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem', textAlign: 'center' }}>
          Choose how you'd like to send your request:
        </p>
        <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3">
          <button
            className="contact-chip whatsapp"
            onClick={() => sendTo('whatsapp')}
            disabled={!isValid || sending}
            style={{ opacity: isValid ? 1 : 0.5, cursor: isValid ? 'pointer' : 'not-allowed' }}
          >
            <span style={{ fontSize: '1.2rem' }}>💬</span>
            <span>
              <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>WhatsApp</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>+39 329 425 8480</div>
            </span>
          </button>

          <button
            className="contact-chip messenger"
            onClick={() => sendTo('messenger')}
            disabled={!isValid || sending}
            style={{ opacity: isValid ? 1 : 0.5, cursor: isValid ? 'pointer' : 'not-allowed' }}
          >
            <span style={{ fontSize: '1.2rem' }}>📨</span>
            <span>
              <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Messenger</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Facebook Chat</div>
            </span>
          </button>
        </div>

        {!isValid && (
          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
            Please enter your name to continue.
          </p>
        )}
      </div>
    </div>
  );
}

export default function AppointmentCTA({ onBook }) {
  return (
    <section
      id="booking"
      style={{
        position: 'relative', overflow: 'hidden',
        padding: '8rem 0',
        background: 'linear-gradient(135deg, #1A1218 0%, #2A1820 35%, #1A1218 100%)',
      }}
    >
      {/* Liquid blobs */}
      <div style={{
        position: 'absolute', width: '600px', height: '600px',
        background: 'radial-gradient(ellipse, rgba(183,110,121,0.25) 0%, transparent 70%)',
        top: '-150px', left: '-100px', borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
        animation: 'liquidBlob 12s ease-in-out infinite', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: '400px', height: '400px',
        background: 'radial-gradient(ellipse, rgba(232,180,184,0.15) 0%, transparent 70%)',
        bottom: '-80px', right: '-60px', borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
        animation: 'liquidBlob 16s ease-in-out infinite reverse', pointerEvents: 'none',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        {/* Tag */}
        <div className="badge" style={{
          margin: '0 auto 2rem',
          background: 'rgba(183,110,121,0.15)',
          border: '1px solid rgba(183,110,121,0.3)',
          color: '#E8B4B8',
          width: 'fit-content',
        }}>
          ✦ Personal Shopping Experience
        </div>

        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(2rem, 5vw, 4rem)',
          fontWeight: 400,
          color: '#fff',
          lineHeight: 1.1,
          marginBottom: '1.5rem',
          maxWidth: '700px',
          margin: '0 auto 1.5rem',
        }}>
          Come Visit Us in <em style={{
            fontStyle: 'italic', fontWeight: 300,
            background: 'linear-gradient(135deg, #E8B4B8, #B76E79)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>Beautiful Venice</em>
        </h2>

        <p style={{
          fontSize: '1rem', color: 'rgba(255,255,255,0.65)',
          maxWidth: '520px', margin: '0 auto 3.5rem', lineHeight: 1.75,
        }}>
          Book a personal appointment and let us guide you through our exclusive collection.
          We speak English, Italian, Hindi & Urdu.
        </p>

        {/* Contact chips */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10 w-full">
          {/* Book appointment */}
          <button
            className="btn-primary w-full sm:w-auto"
            onClick={onBook}
            style={{ fontSize: '0.85rem' }}
          >
            📅 Book an Appointment
          </button>

          {/* WhatsApp */}
          <a
            href={`https://wa.me/${BUSINESS_INFO.whatsappRaw}?text=${encodeURIComponent('Hi Isha Boutique! I saw your website and would love to know more.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glass w-full sm:w-auto"
            style={{ fontSize: '0.85rem' }}
          >
            💬 WhatsApp Us
          </a>

          {/* Messenger */}
          <a
            href={BUSINESS_INFO.messenger}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glass w-full sm:w-auto"
            style={{ fontSize: '0.85rem' }}
          >
            📨 Message on Facebook
          </a>
        </div>

        {/* Contact chips info */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
          {[
            { icon: '📍', label: 'Via Cappuccina 15/c, Mestre' },
            { icon: '📞', label: BUSINESS_INFO.phone },
            { icon: '🕐', label: 'Mon–Sat · 10:00–19:00' },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', fontWeight: 500,
            }}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export { BookingModal };
